import { Context } from "@/App";
import useDialog from "@/base-components/hooks/useDialog";
import Icon, { Icons } from "@/base-components/Icons";
import Table, { TableProps } from "@/base-components/Table";
import Text from "@/base-components/Text";
import { RoutePath, SPACING } from "@/generic/config";
import { Customer } from "@/model/customer";
import { CustomerHit } from "@/model/customer-hit-schema";
import { Box, Flex, IconButton } from "@chakra-ui/react";
import React from "react";
import { CustomerSearchWithResults } from "../customer/CustomerSearchPage";
import { ValidationMessage } from "@/base-components/hooks/useModel";
import Tooltip from "@/base-components/Tooltip";

/**
 * A component that displays a customer in a table. Primarily used to show a connected customer in a table.
 */
const ConnectedCustomerBox = ({ customer, validationMessage, showTitle = true, onConnect }: { customer?: CustomerHit | Customer, validationMessage?: ValidationMessage<any>, showTitle?: boolean, onConnect?: (customer: CustomerHit) => void }) => {
    const { translations } = React.useContext(Context);
    const hasCustomer = (customer && customer.id > 0) ?? false;
    const dialog = useDialog();
    const onSearchCustomer = () => {
        dialog.openDialog({
            title: translations.model.customer.search.title,
            body: <CustomerSearchWithResults onRowClick={(row: CustomerHit) => {
                onConnect?.(row);
                dialog.closeDialog();
            }} />,
            buttons: [],
            size: "lg",
            forcedHeight: "50vh",
        });
    }

    const tableProps: TableProps<Customer | CustomerHit> = {
        columns: [
            { title: translations.model.customer.id, scale: 1, getter: (r) => r.id },
            { title: translations.model.customer.externalId, scale: 1, getter: (r) => r.externalId },
            { title: translations.model.customer.fullName, scale: 1, getter: (r) => `${r.firstName} ${r.lastName}` },
            { title: translations.model.customer.identificationNumber.id, scale: 1, getter: (r) => r.identificationNumber },
        ],
        rows: customer?.id ? [customer] : [],
        href: (row) => `${RoutePath.Customers}/${row.id}`,
    };

    return (
        <Box>
            {showTitle && <Text variant="title">{translations.model.worksite.customer}</Text>}
            {hasCustomer && (
                <Box>
                    <Table tableProps={tableProps} />
                </Box>
            )}
            {!hasCustomer && (
                <Flex alignItems="center">
                    <Text>{translations.model.worksite.noCustomerConnected}</Text>
                    <Tooltip tooltip={translations.model.worksite[onConnect ? "noCustomerConnected" : "noWorksiteSoNoCustomer"]} >
                        <Icon size="xs" icon={Icons.Warning} color={onConnect ? "red" : "orange"} ml={SPACING} />
                    </Tooltip>
                </Flex>
            )}
            <Flex justifyContent="flex-end">
                {onConnect && (
                    <IconButton onClick={onSearchCustomer}>
                        <Icon icon={Icons.Search} />
                    </IconButton>
                )}
            </Flex>
        </Box>
    );
};

export default ConnectedCustomerBox;
